<template>
  <div class="chat-window">
    <!-- 头部 - 显示对方信息 -->
    <div class="chat-header">
      <div v-if="currentSession" class="header-user-info">
        <img :src="currentSession.target_user_avatar || defaultAvatar" :alt="currentSession.target_user_name" class="user-avatar" />
        <div class="user-info">
          <div class="user-name">{{ currentSession.target_user_name }}</div>
          <div class="chat-type">
            <Tag :color="currentSession.chat_type === 1 ? 'blue' : 'green'" size="small">
              {{ currentSession.chat_type === 1 ? '职位沟通' : '简历沟通' }}
            </Tag>
          </div>
        </div>
      </div>
      <div v-else class="header-placeholder">
        请选择会话开始聊天
      </div>
    </div>

    <!-- 消息列表区域 -->
    <div class="chat-messages" ref="messagesContainer" v-loading="loading">
      <!-- 加载更多历史消息 -->
      <div v-if="hasMoreMessages && messages.length > 0" class="load-more-history" @click="loadMoreMessages">
        <span v-if="!loadingMore">加载更多历史消息</span>
        <Spin v-else size="small"></Spin>
      </div>

      <!-- 消息列表 -->
      <div v-for="(message, index) in messages" :key="message.message_id" class="message-wrapper">
        <!-- 时间分隔 -->
        <div v-if="shouldShowTime(index)" class="message-time-divider">
          {{ formatMessageTime(message.created_at) }}
        </div>

        <!-- 消息内容 -->
        <div class="message-item" :class="{ 'is-mine': message.sender_id === userId }">
          <!-- 头像 -->
          <div class="message-avatar">
            <img :src="getMessageAvatar(message)" :alt="getMessageName(message)" />
          </div>

          <!-- 消息气泡 -->
          <div class="message-content">
            <div class="message-name">{{ getMessageName(message) }}</div>

            <!-- 文本消息 -->
            <div v-if="message.message_type === 1" class="message-bubble text-message">
              {{ message.content }}
            </div>

            <!-- 图片消息 -->
            <div v-else-if="message.message_type === 2" class="message-bubble image-message">
              <img :src="message.image_url" @click="previewImage(message.image_url)" />
            </div>

            <!-- 系统消息 -->
            <div v-else-if="message.message_type === 3" class="system-message">
              {{ message.content }}
            </div>
          </div>
        </div>
      </div>

      <!-- 空状态 -->
      <div v-if="!loading && messages.length === 0 && currentSession" class="empty-messages">
        <Icon type="ios-chatbubbles-outline" size="60" color="#c5c8ce" />
        <p>暂无消息，发送一条消息开始聊天吧</p>
      </div>
    </div>

    <!-- 输入区域 -->
    <div class="chat-input" v-if="currentSession">
      <div class="input-toolbar">
        <Upload
          :action="uploadAction"
          :show-upload-list="false"
          :before-upload="beforeUpload"
          :on-success="handleUploadSuccess"
          :on-error="handleUploadError"
        >
          <Button icon="ios-image" size="small" type="text">图片</Button>
        </Upload>
      </div>

      <div class="input-area">
        <Input
          v-model="inputMessage"
          type="textarea"
          :autosize="{ minRows: 3, maxRows: 5 }"
          placeholder="请输入消息内容..."
          @on-enter="handleSendMessage"
        />
      </div>

      <div class="input-actions">
        <Button type="primary" :loading="sending" @click="handleSendMessage">
          发送 (Enter)
        </Button>
      </div>
    </div>
  </div>
</template>

<script>
import { getChatMessages, sendChatMessage, markMessagesRead, uploadChatImage } from '@/api/chat'
import { mapState } from 'vuex'
import moment from 'moment'

export default {
  name: 'ChatWindow',
  props: {
    currentSession: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      messages: [],
      inputMessage: '',
      loading: false,
      loadingMore: false,
      sending: false,
      currentPage: 1,
      pageSize: 30,
      hasMoreMessages: true,
      defaultAvatar: 'https://file.iviewui.com/dist/a0e88e83800f138b94d2414621bd9704.png',
      uploadAction: '/v1/chat/upload_image',
      pollingTimer: null,
      pollingInterval: 3000 // 3秒轮询一次
    }
  },
  computed: {
    ...mapState({
      userId: state => state.user.userId,
      userName: state => state.user.userName,
      userAvatar: state => state.user.avatorImgPath
    })
  },
  watch: {
    currentSession: {
      handler (newSession, oldSession) {
        if (newSession && newSession.session_id !== (oldSession && oldSession.session_id)) {
          this.switchSession()
        }
      },
      immediate: true
    }
  },
  beforeDestroy () {
    this.stopPolling()
  },
  methods: {
    /**
     * 切换会话
     */
    async switchSession () {
      this.stopPolling()
      this.messages = []
      this.currentPage = 1
      this.hasMoreMessages = true

      if (this.currentSession) {
        await this.loadMessages()
        await this.markAsRead()
        this.startPolling()
      }
    },

    /**
     * 加载消息列表
     */
    async loadMessages (append = false) {
      if (!this.currentSession) return

      if (!append) {
        this.loading = true
      } else {
        this.loadingMore = true
      }

      try {
        const params = {
          session_id: this.currentSession.session_id,
          page: this.currentPage,
          page_size: this.pageSize
        }

        const res = await getChatMessages(params)

        if (res.data.code === 0) {
          const newMessages = res.data.data.messages || []

          if (append) {
            // 加载历史消息，插入到前面
            this.messages = [...newMessages.reverse(), ...this.messages]
          } else {
            this.messages = newMessages.reverse()
            this.$nextTick(() => {
              this.scrollToBottom()
            })
          }

          this.hasMoreMessages = newMessages.length >= this.pageSize
        } else {
          this.$Message.error(res.data.msg || '加载消息失败')
        }
      } catch (error) {
        console.error('加载消息失败:', error)
        this.$Message.error('加载消息失败')
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },

    /**
     * 加载更多历史消息
     */
    loadMoreMessages () {
      if (this.loadingMore || !this.hasMoreMessages) return
      this.currentPage++
      this.loadMessages(true)
    },

    /**
     * 发送消息
     */
    async handleSendMessage () {
      if (!this.inputMessage.trim() || this.sending) return

      const content = this.inputMessage.trim()
      this.sending = true

      try {
        const data = {
          session_id: this.currentSession.session_id,
          message_type: 1, // 文本消息
          content: content
        }

        const res = await sendChatMessage(data)

        if (res.data.code === 0) {
          // 添加消息到列表
          const newMessage = res.data.data.message
          this.messages.push(newMessage)

          // 清空输入框
          this.inputMessage = ''

          // 滚动到底部
          this.$nextTick(() => {
            this.scrollToBottom()
          })

          // 通知父组件更新会话列表
          this.$emit('message-sent', {
            sessionId: this.currentSession.session_id,
            message: newMessage
          })
        } else {
          this.$Message.error(res.data.msg || '发送失败')
        }
      } catch (error) {
        console.error('发送消息失败:', error)
        this.$Message.error('发送失败')
      } finally {
        this.sending = false
      }
    },

    /**
     * 上传图片前的校验
     */
    beforeUpload (file) {
      const isImage = file.type.startsWith('image/')
      const isLt5M = file.size / 1024 / 1024 < 5

      if (!isImage) {
        this.$Message.error('只能上传图片文件')
        return false
      }

      if (!isLt5M) {
        this.$Message.error('图片大小不能超过 5MB')
        return false
      }

      return true
    },

    /**
     * 图片上传成功
     */
    async handleUploadSuccess (response) {
      if (response.code === 0) {
        const imageUrl = response.data.image_url

        try {
          const data = {
            session_id: this.currentSession.session_id,
            message_type: 2, // 图片消息
            image_url: imageUrl
          }

          const res = await sendChatMessage(data)

          if (res.data.code === 0) {
            const newMessage = res.data.data.message
            this.messages.push(newMessage)

            this.$nextTick(() => {
              this.scrollToBottom()
            })

            this.$emit('message-sent', {
              sessionId: this.currentSession.session_id,
              message: newMessage
            })
          } else {
            this.$Message.error(res.data.msg || '发送图片失败')
          }
        } catch (error) {
          console.error('发送图片失败:', error)
          this.$Message.error('发送图片失败')
        }
      } else {
        this.$Message.error(response.msg || '图片上传失败')
      }
    },

    /**
     * 图片上传失败
     */
    handleUploadError () {
      this.$Message.error('图片上传失败')
    },

    /**
     * 标记消息为已读
     */
    async markAsRead () {
      if (!this.currentSession || this.currentSession.unread_count === 0) return

      try {
        const data = {
          session_id: this.currentSession.session_id
        }

        await markMessagesRead(data)

        // 通知父组件更新会话列表
        this.$emit('messages-read', this.currentSession.session_id)
      } catch (error) {
        console.error('标记已读失败:', error)
      }
    },

    /**
     * 开始轮询新消息
     */
    startPolling () {
      this.pollingTimer = setInterval(() => {
        this.checkNewMessages()
      }, this.pollingInterval)
    },

    /**
     * 停止轮询
     */
    stopPolling () {
      if (this.pollingTimer) {
        clearInterval(this.pollingTimer)
        this.pollingTimer = null
      }
    },

    /**
     * 检查新消息
     */
    async checkNewMessages () {
      if (!this.currentSession || this.messages.length === 0) return

      try {
        const params = {
          session_id: this.currentSession.session_id,
          page: 1,
          page_size: 10
        }

        const res = await getChatMessages(params)

        if (res.data.code === 0) {
          const latestMessages = res.data.data.messages || []
          const lastMessage = this.messages[this.messages.length - 1]
          const lastMessageId = lastMessage && lastMessage.message_id

          // 找到新消息
          const newMessages = latestMessages.filter(msg => msg.message_id > lastMessageId)

          if (newMessages.length > 0) {
            this.messages.push(...newMessages.reverse())

            this.$nextTick(() => {
              this.scrollToBottom()
            })

            // 自动标记已读
            await this.markAsRead()
          }
        }
      } catch (error) {
        console.error('检查新消息失败:', error)
      }
    },

    /**
     * 滚动到底部
     */
    scrollToBottom () {
      const container = this.$refs.messagesContainer
      if (container) {
        container.scrollTop = container.scrollHeight
      }
    },

    /**
     * 预览图片
     */
    previewImage (url) {
      // 使用 iView 的图片预览功能
      window.open(url, '_blank')
    },

    /**
     * 是否显示时间分隔
     */
    shouldShowTime (index) {
      if (index === 0) return true

      const current = this.messages[index]
      const previous = this.messages[index - 1]

      // 时间间隔超过5分钟显示
      return (current.created_at - previous.created_at) > 300
    },

    /**
     * 格式化消息时间
     */
    formatMessageTime (timestamp) {
      return moment(timestamp * 1000).format('YYYY-MM-DD HH:mm')
    },

    /**
     * 获取消息头像
     */
    getMessageAvatar (message) {
      if (message.sender_id === this.userId) {
        return this.userAvatar || this.defaultAvatar
      }
      return (this.currentSession && this.currentSession.target_user_avatar) || this.defaultAvatar
    },

    /**
     * 获取消息发送者名称
     */
    getMessageName (message) {
      if (message.sender_id === this.userId) {
        return this.userName || '我'
      }
      return (this.currentSession && this.currentSession.target_user_name) || '对方'
    }
  }
}
</script>

<style lang="less" scoped>
.chat-window {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f5f5f5;

  .chat-header {
    padding: 16px 20px;
    background: #fff;
    border-bottom: 1px solid #e8eaec;

    .header-user-info {
      display: flex;
      align-items: center;

      .user-avatar {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        margin-right: 12px;
      }

      .user-info {
        .user-name {
          font-size: 16px;
          font-weight: 500;
          color: #17233d;
          margin-bottom: 4px;
        }

        .chat-type {
          font-size: 12px;
        }
      }
    }

    .header-placeholder {
      text-align: center;
      color: #808695;
      font-size: 14px;
    }
  }

  .chat-messages {
    flex: 1;
    overflow-y: auto;
    padding: 20px;

    .load-more-history {
      text-align: center;
      padding: 10px;
      color: #2d8cf0;
      cursor: pointer;
      font-size: 14px;
      margin-bottom: 20px;

      &:hover {
        color: #5cadff;
      }
    }

    .message-wrapper {
      margin-bottom: 20px;

      .message-time-divider {
        text-align: center;
        font-size: 12px;
        color: #808695;
        margin: 20px 0;
      }

      .message-item {
        display: flex;
        margin-bottom: 16px;

        &.is-mine {
          flex-direction: row-reverse;

          .message-content {
            align-items: flex-end;

            .message-name {
              text-align: right;
            }

            .message-bubble {
              background: #2d8cf0;
              color: #fff;
            }
          }
        }

        .message-avatar {
          margin: 0 12px;
          flex-shrink: 0;

          img {
            width: 40px;
            height: 40px;
            border-radius: 50%;
          }
        }

        .message-content {
          display: flex;
          flex-direction: column;
          max-width: 60%;

          .message-name {
            font-size: 12px;
            color: #808695;
            margin-bottom: 6px;
          }

          .message-bubble {
            padding: 10px 14px;
            border-radius: 8px;
            word-break: break-word;

            &.text-message {
              background: #fff;
              color: #17233d;
              font-size: 14px;
              line-height: 1.6;
            }

            &.image-message {
              padding: 4px;
              background: #fff;

              img {
                max-width: 300px;
                max-height: 300px;
                border-radius: 4px;
                cursor: pointer;
                display: block;
              }
            }
          }

          .system-message {
            padding: 8px 12px;
            background: #f0f0f0;
            color: #808695;
            font-size: 12px;
            border-radius: 4px;
            text-align: center;
          }
        }
      }
    }

    .empty-messages {
      text-align: center;
      padding: 100px 20px;
      color: #c5c8ce;

      p {
        margin-top: 16px;
        font-size: 14px;
      }
    }
  }

  .chat-input {
    background: #fff;
    border-top: 1px solid #e8eaec;
    padding: 12px 20px;

    .input-toolbar {
      margin-bottom: 8px;
    }

    .input-area {
      margin-bottom: 12px;
    }

    .input-actions {
      text-align: right;
    }
  }
}
</style>
